import {
  FurnitureSchema,
  FurnitureListResponseSchema,
  FurnitureBrandsResponseSchema,
  type Furniture,
  type FurnitureBrandsResponse,
  type FurnitureListQueryInput,
  type FurnitureListResponse,
} from '../contracts/furniture';
import { ShopsResponseSchema, type ShopsResponse } from '../contracts/handles';
import type { QueryValue, RequestFn } from './client';

/**
 * Namespace `client.furniture` — обёртки над эндпоинтами Блока 5, под-сценарий
 * «Фурнитура и магазины». Спека — ADR-0068 §2/§3. Зеркалит `handles.ts`.
 *
 * `globalId` в path — `encodeURIComponent` (P-001 verbatim round-trip: `/`, `(`, `)`,
 * пробелы; handler делает `decodeURIComponent` и получает ровно отправленное).
 */
export interface FurnitureApi {
  list(query: FurnitureListQueryInput): Promise<FurnitureListResponse>;
  /**
   * Все distinct-бренды каталога (для скоупа `<select>` на Экране 2.5.7), отсортированы
   * по `brandName`. Серверная операция: клиент не может собрать их из `list` —
   * `limit` ограничен 100, а каталог засеян по брендам подряд (ADR-0077). Guest.
   */
  brands(): Promise<FurnitureBrandsResponse>;
  get(globalId: string): Promise<Furniture>;
  /**
   * Sensitive: требует JWT (401 для гостя, soft-gate P-009). `city` обязателен —
   * бэк отдаёт дилеров этого города с кодом бренда товара в `brands` (иначе
   * `{ shops: [] }`, empty-состояние). Код бренда резолвится СЕРВЕРНО из строки
   * товара по `globalId` — клиент его не шлёт. Список городов — `dealers.cities()`.
   */
  shops(globalId: string, city: string): Promise<ShopsResponse>;
}

export function createFurnitureApi(request: RequestFn): FurnitureApi {
  return {
    list: (query) =>
      request({
        method: 'GET',
        path: '/api/furniture',
        query: queryToParams(query),
        responseSchema: FurnitureListResponseSchema,
      }),

    brands: () =>
      request({
        method: 'GET',
        path: '/api/furniture/brands',
        responseSchema: FurnitureBrandsResponseSchema,
      }),

    get: (globalId) =>
      request({
        method: 'GET',
        path: `/api/furniture/${encodeURIComponent(globalId)}`,
        responseSchema: FurnitureSchema,
      }),

    shops: (globalId, city) =>
      request({
        method: 'GET',
        path: `/api/furniture/${encodeURIComponent(globalId)}/shops`,
        query: { city },
        responseSchema: ShopsResponseSchema,
        auth: true,
      }),
  };
}

function queryToParams(q: FurnitureListQueryInput): Record<string, QueryValue> {
  return {
    name: q.name,
    brand: q.brand,
    cursor: q.cursor,
    limit: q.limit !== undefined ? Number(q.limit) : undefined,
  };
}
